// Catalog.ID Client — `bitcash-auth-token` consent dialog (§4.10, §4.13)
// ============================================================================
// WHAT THE MEMBER IS BEING ASKED
//
// Not "may this module read a key" but "may this module SPEND". That is a
// different question and it gets a different dialog: the four scope buttons the
// key families use are here, because a member who pays for storage every day
// should not be prompted every day — but everything above them names money.
//
// WHAT THE DIALOG MUST NAME, and why each line is here:
//
//   * the REQUESTER — the module that will hold the token. It is the hub's
//     attested id (rendered, never trusted), because that is who the
//     authorisation is issued to and who Settings will later revoke.
//   * WHICH WALLET. The picker is not a convenience: an owner or treasurer
//     delegate can spend the ORGANIZATION's money here (D14), and "which purse"
//     is the first thing a person asks about a payment. The organization's row
//     says so in words, never by colour alone.
//   * the CAPS and the EXPIRY — what the token can do before it must be renewed
//     or is refused. For the shared default token these are the NODE's caps
//     (D27), not the requester's, and the dialog says which it is showing.
//   * the PAYEE. A module's own token pays the one receive address it named
//     (D11). The shared default token names none: it pays Catalog services, at
//     any operator (apps/docs/plans/second-node-and-bitcash-open-issues.md §2.1,
//     §9.3) — on the v2 ledger a token pays only an Operator's wallet, never a
//     member's or the module's own — and that is worth a line.
//   * the SERVICE TYPES the token will carry — for the default token the node's
//     list (what keeps it from paying content), for a module's own token the ones
//     it asked for — and the SITES the module asked for.
//   * that this is REVOCABLE, in Settings, and that revoking cuts the module
//     off. A permission a member cannot see themselves withdrawing is not a
//     permission they meaningfully gave.
//
// The dialog is deliberately silent about balances: it has none (the bag is
// closed at this point, and reading one would need the wallet key), and a
// balance shown here would be a number the member could mistake for the amount
// being authorised.

import type { ShellAPI } from '@catalog-shell';
import { createFieldGroup, createFormRow, createElement } from '@catalog-shell';
import type { BitcashConsentInput, BitcashConsentResult } from './bitcash-types';
import { displayWalletKey } from '../crypto/bitcash';

export function showBitcashAuthTokenConsent(
    shell: ShellAPI,
    input: BitcashConsentInput,
): Promise<BitcashConsentResult> {
    return new Promise<BitcashConsentResult>((resolve) => {
        let resolved = false;
        const resolveOnce = (result: BitcashConsentResult): void => {
            if (resolved) return;
            resolved = true;
            handle.close();
            resolve(result);
        };

        const container = createElement('div', { className: 'cs-entity-view cs-modal-body' });

        container.appendChild(createElement('h2', {
            className: 'cs-modal-title',
            textContent: `Let ${input.requesterLabel || input.requesterId} pay from your bitcash wallet?`,
        }));

        // ── who is asking ───────────────────────────────────────────────────
        const requesterGroup = createFieldGroup({ title: 'Requester' });

        const moduleRow = createFormRow({ label: 'Module' });
        moduleRow.content.appendChild(createElement('div', {
            className: 'cs-field-view-text',
            textContent: input.requesterLabel || input.requesterId,
        }));
        requesterGroup.body.appendChild(moduleRow.element);

        const accountRow = createFormRow({ label: 'Catalog.ID account' });
        accountRow.content.appendChild(createElement('code', {
            className: 'cs-field-view-text',
            textContent: input.owningCid,
        }));
        requesterGroup.body.appendChild(accountRow.element);
        container.appendChild(requesterGroup.element);

        // ── which wallet ────────────────────────────────────────────────────
        const walletGroup = createFieldGroup({ title: 'Pay from' });
        const walletRow = createFormRow({ label: 'Wallet' });
        const select = createElement('select', { className: 'cid-auth-input' }) as HTMLSelectElement;
        for (const choice of input.choices) {
            const option = createElement('option', {
                textContent: choice.kind === 'org' ? `${choice.label} (organization money)` : choice.label,
                attributes: { value: choice.id },
            }) as HTMLOptionElement;
            select.appendChild(option);
        }
        select.value = input.choices[0]?.id ?? '';
        select.disabled = input.choices.length <= 1;
        walletRow.content.appendChild(select);
        walletGroup.body.appendChild(walletRow.element);

        // The common case: the bag is closed (the wallet key is never stored on
        // a device, D5), so the picker names the slot rather than the wallet's
        // own label. Saying so is better than showing a name that may not be
        // the one the member gave it.
        if (input.choices[0]?.provisional) {
            const noteRow = createFormRow({ label: '' });
            noteRow.content.appendChild(createElement('div', {
                className: 'cs-field-view-text',
                textContent: 'Your wallet names are in your key bag; you will be asked for your '
                    + 'supersecret before anything is issued.',
            }));
            walletGroup.body.appendChild(noteRow.element);
        }
        container.appendChild(walletGroup.element);

        // ── what the token is ───────────────────────────────────────────────
        const tokenGroup = createFieldGroup({ title: 'The spending permission' });

        const labelRow = createFormRow({ label: 'Named' });
        labelRow.content.appendChild(createElement('code', { textContent: input.tokenLabel }));
        tokenGroup.body.appendChild(labelRow.element);

        const payeeRow = createFormRow({ label: 'May pay' });
        payeeRow.content.appendChild(createElement('div', {
            className: 'cs-field-view-text',
            textContent: payeeText(input.scope.payee),
        }));
        tokenGroup.body.appendChild(payeeRow.element);

        const caps = input.effectiveCaps ?? {
            dailyCap: input.scope.dailyCap ?? 0,
            monthlyCap: input.scope.monthlyCap ?? 0,
        };
        const dailyRow = createFormRow({ label: 'Up to, per day' });
        dailyRow.content.appendChild(createElement('div', {
            className: 'cs-field-view-text',
            textContent: `${formatBits(caps.dailyCap)} bits`,
        }));
        tokenGroup.body.appendChild(dailyRow.element);

        const monthlyRow = createFormRow({ label: 'Up to, per month' });
        monthlyRow.content.appendChild(createElement('div', {
            className: 'cs-field-view-text',
            textContent: `${formatBits(caps.monthlyCap)} bits`,
        }));
        tokenGroup.body.appendChild(monthlyRow.element);

        const expiryRow = createFormRow({ label: 'Expires' });
        expiryRow.content.appendChild(createElement('div', {
            className: 'cs-field-view-text',
            textContent: input.scope.expiresAt === null
                ? 'Never — until you revoke it'
                : new Date(input.scope.expiresAt).toLocaleString(),
        }));
        tokenGroup.body.appendChild(expiryRow.element);

        if (input.scope.sites.length > 0) {
            const sitesRow = createFormRow({ label: 'Sites' });
            sitesRow.content.appendChild(createElement('div', {
                className: 'cs-field-view-text',
                textContent: input.scope.sites.join(', '),
            }));
            tokenGroup.body.appendChild(sitesRow.element);
        }
        const shownTypes = serviceTypesShown(input);
        if (shownTypes.length > 0) {
            const typesRow = createFormRow({ label: 'For' });
            typesRow.content.appendChild(createElement('div', {
                className: 'cs-field-view-text',
                textContent: shownTypes.join(', '),
            }));
            tokenGroup.body.appendChild(typesRow.element);
        }
        container.appendChild(tokenGroup.element);

        // ── what it means ───────────────────────────────────────────────────
        container.appendChild(createElement('div', {
            className: 'cs-inline-warn',
            textContent: input.isDefaultToken
                ? 'This is a spending permission, not a key: the module may debit the wallet above, '
                  + 'up to these amounts, for the Catalog services listed under "For", at any '
                  + 'operator — never a member\'s wallet, and never content. This is the one '
                  + 'permission every Catalog service shares. Your wallet key stays in your key bag. '
                  + 'You can revoke this permission at any time in Settings → Wallets, which cuts '
                  + 'every service holding it off immediately.'
                : 'This is a spending permission, not a key: the module may debit the wallet above, '
                  + 'up to these amounts, and only in favour of the wallet named under "May pay". '
                  + 'Your wallet key stays in your key bag. You can revoke this permission at any '
                  + 'time in Settings → Wallets, which cuts the module off immediately. Because '
                  + 'this one is the module\'s own, bound to the payee it named, you will be shown '
                  + 'these amounts again whenever the module needs a new permission — "allow always" '
                  + 'remembers the wallet, never the amounts.',
        }));

        // ── the four decisions ──────────────────────────────────────────────
        const actions = createElement('div', { className: 'cs-form-actions' });
        const picked = (): string => select.value || input.choices[0]?.id || '';

        const btnDeny = createElement('button', {
            className: 'cs-btn cs-btn--no',
            textContent: 'Deny',
        }) as HTMLButtonElement;
        btnDeny.addEventListener('click', () => resolveOnce({ decision: 'deny' }));
        actions.appendChild(btnDeny);

        const btnOnce = createElement('button', {
            className: 'cs-btn',
            textContent: 'Allow once',
        }) as HTMLButtonElement;
        btnOnce.addEventListener('click', () => resolveOnce({ decision: 'once', walletId: picked() }));
        actions.appendChild(btnOnce);

        const btnSession = createElement('button', {
            className: 'cs-btn',
            textContent: 'Allow for this session',
        }) as HTMLButtonElement;
        btnSession.addEventListener('click', () => resolveOnce({ decision: 'session', walletId: picked() }));
        actions.appendChild(btnSession);

        const btnAlways = createElement('button', {
            className: 'cs-btn cs-btn--yes',
            textContent: 'Allow always',
        }) as HTMLButtonElement;
        btnAlways.addEventListener('click', () => resolveOnce({ decision: 'always', walletId: picked() }));
        actions.appendChild(btnAlways);

        container.appendChild(actions);

        const handle = shell.showModal(container, {
            size: 'wide',
            dismissOnOverlayClick: false,
            dismissOnEscape: false,
            // A dismissed dialog is a refusal — never a silent approval, and
            // never a hang: the requesting module is waiting on this promise.
            onClose: () => resolveOnce({ decision: 'deny' }),
        });
    });
}

/** The payee in words. The default token names none: it pays Catalog services, at any operator
 *  (§9.3). A FUND address is shown in its typeable display form so a member can compare it with
 *  one they were given. */
export function payeeText(payee: string): string {
    if (payee === 'operator') return 'Catalog services, at any operator';
    return displayWalletKey(payee) ?? payee;
}

/** The service types the dialog lists: the ones the token will CARRY — the node's list for the
 *  default token, which ignores the module's — else the ones the module asked for. */
export function serviceTypesShown(input: BitcashConsentInput): string[] {
    if (input.isDefaultToken && input.effectiveCaps) return [...input.effectiveCaps.serviceTypes];
    return [...input.scope.serviceTypes];
}

function formatBits(value: number): string {
    return Number.isFinite(value) ? Math.floor(value).toLocaleString() : '0';
}
